import React, { useState } from 'react';
import { 
  Users, 
  Search, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Plus,
  MessageCircle,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Upload,
  Image as ImageIcon,
  X,
  Linkedin,
  Calendar,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const ClientDirectory = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [clientForm, setClientForm] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    professionalEmail: '',
    personalEmail: '',
    mobile: '',
    photo: null,
    city: '',
    state: '',
    country: 'India',
    linkedIn: '',
    notes: ''
  });

  const [participantForm, setParticipantForm] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    email: '',
    mobile: '',
    programAttended: '',
    date: '',
    linkedIn: ''
  });

  // Mock client data
  const clients = [
    {
      id: 1,
      firstName: "John",
      lastName: "Smith",
      organization: "TechCorp Solutions",
      city: "New York",
      state: "NY",
      country: "USA",
      professionalEmail: "john.smith@techcorp.com",
      personalEmail: "john.smith@gmail.com",
      mobile: "+1 (555) 123-4567",
      status: "active",
      linkedIn: "https://linkedin.com/in/johnsmith",
      photo: null
    },
    {
      id: 2,
      firstName: "Sarah",
      lastName: "Johnson",
      organization: "Global Industries",
      city: "San Francisco",
      state: "CA",
      country: "USA",
      professionalEmail: "sarah.j@globalind.com",
      personalEmail: "",
      mobile: "+1 (555) 987-6543",
      status: "prospect",
      linkedIn: "",
      photo: null
    }
  ];

  // Mock participant data
  const participants = [
    {
      id: 1,
      firstName: "Michael",
      lastName: "Chen",
      organization: "Innovation Labs",
      email: "m.chen@innovationlabs.com",
      mobile: "+1 (555) 456-7890",
      programAttended: "Agile Methodology Workshop",
      date: "2024-09-15",
      linkedIn: "https://linkedin.com/in/michaelchen"
    },
    {
      id: 2,
      firstName: "Emily",
      lastName: "Rodriguez",
      organization: "StartupXYZ",
      email: "emily@startupxyz.com",
      mobile: "+1 (555) 321-0987",
      programAttended: "Leadership Excellence",
      date: "2024-08-20",
      linkedIn: ""
    }
  ];

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Photo size must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setClientForm(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClientSubmit = (e) => {
    e.preventDefault();
    // Validation: First Name and Last Name are required
    // AND (Professional Email OR Mobile) is required
    if (!clientForm.firstName || !clientForm.lastName) {
      alert('First Name and Last Name are required');
      return;
    }
    if (!clientForm.professionalEmail && !clientForm.mobile) {
      alert('Either Professional Email or Mobile is required');
      return;
    }
    
    console.log('Client data:', clientForm);
    alert('Client added successfully!');
    setShowAddClientModal(false);
    resetClientForm();
  };

  const handleParticipantSubmit = (e) => {
    e.preventDefault();
    console.log('Participant data:', participantForm);
    alert('Participant added successfully!');
    setShowAddParticipantModal(false);
    resetParticipantForm();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Handle Excel file upload
      console.log('Excel file:', file);
      alert('Excel file uploaded! Processing participants...');
      setShowUploadModal(false);
    }
  };

  const resetClientForm = () => {
    setClientForm({
      firstName: '',
      lastName: '',
      organization: '',
      professionalEmail: '',
      personalEmail: '',
      mobile: '',
      photo: null,
      city: '',
      state: '',
      country: 'India',
      linkedIn: '',
      notes: ''
    });
    setPhotoPreview(null);
  };

  const resetParticipantForm = () => {
    setParticipantForm({
      firstName: '',
      lastName: '',
      organization: '',
      email: '',
      mobile: '',
      programAttended: '',
      date: '',
      linkedIn: ''
    });
  };

  const filteredClients = clients.filter(client => {
    const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         client.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterBy === 'all' || client.status === filterBy;
    return matchesSearch && matchesFilter;
  });

  const filteredParticipants = participants.filter(participant => {
    const fullName = `${participant.firstName} ${participant.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
           participant.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
           participant.programAttended.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'prospect': return 'bg-blue-100 text-blue-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Client & Participant Directory</h1>
            <p className="text-gray-600">Manage your training clients and participants</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="participants">Participants</TabsTrigger>
          </TabsList>
          
          <div className="flex space-x-2">
            {activeTab === 'clients' && (
              <Button 
                onClick={() => setShowAddClientModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            )}
            {activeTab === 'participants' && (
              <>
                <Button 
                  onClick={() => setShowAddParticipantModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Participant
                </Button>
                <Button 
                  onClick={() => setShowUploadModal(true)}
                  variant="outline"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Excel
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Search and Filter */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {activeTab === 'clients' && (
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="prospect">Prospect</option>
                  <option value="inactive">Inactive</option>
                </select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Clients Tab */}
        <TabsContent value="clients">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <Card key={client.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-12 h-12">
                        {client.photo ? (
                          <AvatarImage src={client.photo} alt={`${client.firstName} ${client.lastName}`} />
                        ) : (
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            {client.firstName[0]}{client.lastName[0]}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {client.firstName} {client.lastName}
                        </h3>
                        <Badge className={getStatusColor(client.status)}>
                          {client.status}
                        </Badge>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <Building className="w-4 h-4 mr-2 text-gray-400" />
                      {client.organization}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {client.professionalEmail}
                    </div>
                    {client.personalEmail && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {client.personalEmail}
                      </div>
                    )}
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {client.mobile}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                      {client.city}, {client.state}, {client.country}
                    </div>
                    {client.linkedIn && (
                      <div className="flex items-center text-sm text-blue-600">
                        <Linkedin className="w-4 h-4 mr-2" />
                        <a href={client.linkedIn} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          LinkedIn Profile
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 mt-4 pt-4 border-t">
                    <Button size="sm" variant="outline" className="flex-1">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Message
                    </Button>
                    <Button size="sm" variant="outline">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants">
          <Card>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Name</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Organization</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Email</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Mobile</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Program Attended</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">LinkedIn</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredParticipants.map((participant) => (
                      <tr key={participant.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center space-x-2">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-blue-100 text-blue-600 text-xs">
                                {participant.firstName[0]}{participant.lastName[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-gray-900">
                              {participant.firstName} {participant.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">{participant.organization}</td>
                        <td className="p-3 text-sm text-gray-600">{participant.email}</td>
                        <td className="p-3 text-sm text-gray-600">{participant.mobile}</td>
                        <td className="p-3 text-sm text-gray-600">{participant.programAttended}</td>
                        <td className="p-3 text-sm text-gray-600">{participant.date}</td>
                        <td className="p-3">
                          {participant.linkedIn ? (
                            <a 
                              href={participant.linkedIn} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm"
                            >
                              <Linkedin className="w-4 h-4" />
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center space-x-2">
                            <button className="text-blue-600 hover:text-blue-800">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Client Modal */}
      <Dialog open={showAddClientModal} onOpenChange={setShowAddClientModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Add New Client</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleClientSubmit} className="space-y-6 mt-4">
            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photo (Optional)
              </label>
              {photoPreview ? (
                <div className="flex items-center space-x-4">
                  <img src={photoPreview} alt="Preview" className="w-24 h-24 rounded-full object-cover border-2" />
                  <Button type="button" variant="outline" onClick={() => { setPhotoPreview(null); setClientForm(prev => ({ ...prev, photo: null })); }}>
                    <X className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:underline text-sm">Change Photo</span>
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </label>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400">
                    <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload photo</p>
                  </div>
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                </label>
              )}
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <Input
                  value={clientForm.firstName}
                  onChange={(e) => setClientForm(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <Input
                  value={clientForm.lastName}
                  onChange={(e) => setClientForm(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Organization */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <Input
                value={clientForm.organization}
                onChange={(e) => setClientForm(prev => ({ ...prev, organization: e.target.value }))}
              />
            </div>

            {/* Emails */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Professional Email *
                </label>
                <Input
                  type="email"
                  value={clientForm.professionalEmail}
                  onChange={(e) => setClientForm(prev => ({ ...prev, professionalEmail: e.target.value }))}
                  placeholder="work@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Email
                </label>
                <Input
                  type="email"
                  value={clientForm.personalEmail}
                  onChange={(e) => setClientForm(prev => ({ ...prev, personalEmail: e.target.value }))}
                  placeholder="personal@email.com"
                />
              </div>
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
              <Input
                type="tel"
                value={clientForm.mobile}
                onChange={(e) => setClientForm(prev => ({ ...prev, mobile: e.target.value }))}
                placeholder="+1 (555) 123-4567"
              />
              <p className="text-xs text-gray-500 mt-1">* Either Professional Email or Mobile is required</p>
            </div>

            {/* Location */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <Input
                  value={clientForm.city}
                  onChange={(e) => setClientForm(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <Input
                  value={clientForm.state}
                  onChange={(e) => setClientForm(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <Input
                  value={clientForm.country}
                  onChange={(e) => setClientForm(prev => ({ ...prev, country: e.target.value }))}
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile (Optional)</label>
              <Input
                type="url"
                value={clientForm.linkedIn}
                onChange={(e) => setClientForm(prev => ({ ...prev, linkedIn: e.target.value }))}
                placeholder="https://linkedin.com/in/..."
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Additional notes about the client..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddClientModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Add Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Participant Modal */}
      <Dialog open={showAddParticipantModal} onOpenChange={setShowAddParticipantModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Add New Participant</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleParticipantSubmit} className="space-y-4 mt-4">
            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <Input
                  value={participantForm.firstName}
                  onChange={(e) => setParticipantForm(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <Input
                  value={participantForm.lastName}
                  onChange={(e) => setParticipantForm(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Organization */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization *</label>
              <Input
                value={participantForm.organization}
                onChange={(e) => setParticipantForm(prev => ({ ...prev, organization: e.target.value }))}
                required
              />
            </div>

            {/* Email and Mobile */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input
                  type="email"
                  value={participantForm.email}
                  onChange={(e) => setParticipantForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                <Input
                  type="tel"
                  value={participantForm.mobile}
                  onChange={(e) => setParticipantForm(prev => ({ ...prev, mobile: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Program and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Program Attended *</label>
                <Input
                  value={participantForm.programAttended}
                  onChange={(e) => setParticipantForm(prev => ({ ...prev, programAttended: e.target.value }))}
                  placeholder="e.g., Leadership Workshop"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <Input
                  type="date"
                  value={participantForm.date}
                  onChange={(e) => setParticipantForm(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile (Optional)</label>
              <Input
                type="url"
                value={participantForm.linkedIn}
                onChange={(e) => setParticipantForm(prev => ({ ...prev, linkedIn: e.target.value }))}
                placeholder="https://linkedin.com/in/..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddParticipantModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Add Participant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Excel Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Upload Participants Excel</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Excel file should contain these columns:</strong>
              </p>
              <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                <li>First Name</li>
                <li>Last Name</li>
                <li>Organization</li>
                <li>Email</li>
                <li>Mobile</li>
                <li>Program Attended</li>
                <li>Date</li>
                <li>LinkedIn (optional)</li>
              </ul>
            </div>

            <label className="cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm font-medium text-gray-700 mb-1">Click to upload Excel file</p>
                <p className="text-xs text-gray-500">XLS or XLSX format</p>
              </div>
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <div className="flex items-center space-x-2">
              <Download className="w-4 h-4 text-blue-600" />
              <a href="#" className="text-sm text-blue-600 hover:underline">
                Download sample Excel template
              </a>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDirectory;